import React, { useRef, useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import "./audioplayer.css";

const AudioPlayer = ({ audioSrc, setData, isDeletingAllowed = true, onLoadedData }) => {
    const audioRef = useRef(null);
    const progressRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const germanRed = {
        50: "#FFF6EC",
        100: "#FFEBD3",
        200: "#FFD2A7",
        300: "#FFB26F",
        400: "#FF8635",
        500: "#FF640D",
        600: "#F34803",
        700: "#CC3405",
        800: "#9F290D",
        900: "#80250E",
        950: "#450F05",
    };

    const germanAccentText = "#36251D";
    const germanYellow = "#FFD700"; // Bright German Yellow

    const handleMetadataLoad = () => {
        const audio = audioRef.current;
        if (audio && isFinite(audio.duration)) {
            setDuration(audio.duration);
        }
    };

    useEffect(() => {
        const audio = audioRef.current;
        const updateTime = () => {
            if (!isDragging) {
                setCurrentTime(audio.currentTime);
            }
        };

        if (audio) {
            audio.addEventListener("timeupdate", updateTime);
            audio.addEventListener("loadedmetadata", handleMetadataLoad);
            return () => {
                audio.removeEventListener("timeupdate", updateTime);
                audio.removeEventListener("loadedmetadata", handleMetadataLoad);
            };
        }
    }, [audioSrc, isDragging]);

    const togglePlayPause = (e) => {
        e.preventDefault();
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleProgressClick = (e) => {
        const progressWidth = progressRef.current.clientWidth;
        const offsetX = e.nativeEvent.offsetX;
        const seekTime = (offsetX / progressWidth) * duration;
        audioRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
    };

    const handleDragStart = () => setIsDragging(true);

    const handleDrag = (e) => {
        if (isDragging && progressRef.current) {
            const progressWidth = progressRef.current.clientWidth;
            const offsetX = e.nativeEvent.offsetX;
            const newTime = (offsetX / progressWidth) * duration;
            setCurrentTime(newTime);
        }
    };

    const handleDragEnd = () => {
        if (isDragging) {
            audioRef.current.currentTime = currentTime;
            setIsDragging(false);
        }
    };

    const formatTime = (time) => {
        if (!isFinite(time) || time === 0) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60)
            .toString()
            .padStart(2, "0");
        return `${minutes}:${seconds}`;
    };

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            className="whatsapp-audio-player en w-full bg-[#FFF6EC] dark:bg-[#450F05] smooth p-3 rounded-lg shadow-md flex items-center space-x-3"
        >
            {/* Speaker Icon with German Yellow */}
            <div className="rounded-full w-14 h-10 bg-[#FFD700] flex items-center justify-center">
                <Icon icon="mdi:account" className="text-[#36251D]" width="20" />
            </div>

            {/* Play/Pause Button in German Red */}
            <button
                onClick={togglePlayPause}
                className="text-[#F34803] p-2 rounded-full focus:outline-none"
            >
                <Icon icon={isPlaying ? "mdi:pause" : "mdi:play"} width="24" />
            </button>

            {/* Progress Bar with German Red Track */}
            <div
                ref={progressRef}
                className="relative flex-grow h-1 bg-[#FFD2A7] rounded-full cursor-pointer"
                onClick={handleProgressClick}
                onMouseDown={handleDragStart}
                onMouseMove={handleDrag}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
            >
                <div
                    className="absolute h-1 bg-[#FF640D] rounded-full"
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                ></div>
                <div
                    className="absolute h-3 w-3 bg-[#FF640D] rounded-full transform -translate-y-1/2 top-1/2 cursor-pointer"
                    style={{ left: `${(currentTime / duration) * 100}%` }}
                    onMouseDown={handleDragStart}
                ></div>
            </div>

            {/* Time Display in German Accent Color */}
            <span className="text-xs w-24 text-[#36251D]">
                {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            {/* Audio Element */}
            <audio
                ref={audioRef}
                src={audioSrc}
                preload="metadata"
                onLoadedData={onLoadedData}
                onError={(e) => {
                    e.target.style.display = "none";
                    document.getElementById("audio-error-message").style.display = "block";
                }}
            />

            {/* Error Message */}
            <span
                id="audio-error-message"
                style={{ display: "none" }}
                className="text-red-600 text-sm mt-2"
            >
                متصفحك الخاص بك لا يدعم ملف الصوت
            </span>

            {/* Delete Button (Optional) */}
            {isDeletingAllowed && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setData((prevData) => ({ ...prevData, audio_source: "" }));
                    }}
                    className="text-[#9F290D] smooth hover:text-[#CC3405] p-2 rounded-full bg-[#FFD2A7] dark:bg-[#80250E]"
                >
                    <Icon width="18" icon="weui:delete-on-filled" className="w-6 h-6" />
                </button>
            )}
        </div>
    );
};

export default AudioPlayer;
